'use client';

import { Suspense, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { authApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, RefreshCw, LogOut, CheckCircle } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { TurnstileWidget } from '@/components/turnstile-widget';

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}

function LoginContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signOut, user, profile, loading: authLoading, refreshProfile } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isWaitingForProfile, setIsWaitingForProfile] = useState(false);
  const [isRevokingOthers, setIsRevokingOthers] = useState(false);
  // True iff this render was triggered by the per-app single-session
  // gate ("signed in from another browser"). Drives the inline
  // sign-out-of-all-devices escape hatch below the error banner.
  const sessionTakenOver = searchParams.get('session_taken_over') === '1';
  // True iff another tab in this browser just claimed the app session
  // and bounced us here (DashboardLayout's broadcast handler). The
  // shared-localStorage tokens are still valid, so the redirect-if-
  // authenticated useEffect below WOULD normally fling us back to '/'
  // and we'd grab the Web Lock again — re-blockading the takeover
  // tab. Suppress that redirect while ?kicked=1 is set and show a
  // calm banner instead. Added 2026-06-03.
  const wasKicked = searchParams.get('kicked') === '1';
  // Cloudflare Turnstile token — required to submit. null means
  // captcha not solved yet (or expired). Widget remount key is bumped
  // after a failed submit so a fresh challenge is issued (Cloudflare
  // bans reuse of consumed tokens).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  // Check for success messages from signup/password reset flows and session expiry
  useEffect(() => {
    const message = searchParams.get('message');
    const sessionExpired = searchParams.get('session_expired');

    if (sessionTakenOver) {
      // Per-app single-session enforcement signaled this — another
      // browser logged into the dashboard with the same account.
      setError('Your account was signed in from another browser. Please sign in again to continue.');
    } else if (sessionExpired === 'true') {
      setError('Your session has expired. Please sign in again.');
    } else if (message === 'account_activated') {
      setSuccessMessage('Your account has been activated! Please sign in with your new password.');
    } else if (message === 'already_activated') {
      setSuccessMessage('Your account is already active. Please sign in.');
    } else if (message === 'password_reset') {
      setSuccessMessage('Your password has been reset successfully! Please sign in with your new password.');
    } else if (message === 'invite_expired') {
      setError('Your invitation link is invalid or has expired. Please ask your administrator to resend the invitation.');
    }
  }, [searchParams]);

  // Redirect to home if already authenticated AND has profile.
  // Redirect pending users to complete password setup.
  // Skip the redirect entirely when ?kicked=1: another tab in this
  // browser just claimed the session and we MUST stay on /login until
  // the user explicitly decides what to do (close this tab, or click
  // the inline link to reclaim ownership from the new tab). Auto-
  // redirecting from here would re-mount DashboardLayout, re-grab the
  // Web Lock, and force the takeover tab back into the blockade screen.
  useEffect(() => {
    if (wasKicked) return;
    if (!authLoading && user && profile) {
      setIsWaitingForProfile(false);
      if (profile.status === 'pending') {
        router.push('/auth/callback?type=invite');
      } else {
        router.push('/home');
      }
    }
  }, [authLoading, user, profile, router, wasKicked]);

  // Show spinner while checking auth or loading profile
  if (authLoading || (user && !profile) || isWaitingForProfile || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // User is authenticated but profile failed to load after loading completes and we've waited - show options
  if (user && !profile && !authLoading && !isWaitingForProfile) {
    const handleRefreshProfile = async () => {
      setIsRefreshing(true);
      await refreshProfile();
      setIsRefreshing(false);
      // If profile loaded, redirect
      if (profile) {
        router.push('/home');
      }
    };

    const handleSignOut = async () => {
      await signOut();
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-xl bg-yellow-500 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">Profile Not Loaded</CardTitle>
            <CardDescription className="text-center">
              You are signed in as <strong>{user.email}</strong>, but your profile could not be loaded.
              This may be due to database configuration issues.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Possible causes:</strong>
              </p>
              <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 list-disc list-inside space-y-1">
                <li>Database migration not completed</li>
                <li>RLS policies blocking access</li>
                <li>User profile not created in users table</li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              onClick={handleRefreshProfile}
              className="w-full"
              variant="outline"
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry Loading Profile
                </>
              )}
            </Button>
            <Button
              onClick={handleSignOut}
              variant="destructive"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out & Re-login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // User is authenticated with profile - show spinner while redirecting
  if (user && profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!captchaToken) {
      setError('Please complete the verification challenge before signing in.');
      return;
    }

    setIsLoading(true);
    setIsWaitingForProfile(false);

    try {
      const { error: signInError, mfaRequired } = await signIn(
        email,
        password,
        captchaToken,
      );

      if (signInError) {
        setError(signInError.message);
        setIsLoading(false);
        setIsWaitingForProfile(false);
        // Consumed token cannot be reused. Force a fresh challenge.
        setCaptchaToken(null);
        setCaptchaResetKey((k) => k + 1);
        return;
      }

      // If the user has a verified MFA factor (backend told us via the
      // signin response) immediately redirect to /auth/mfa-challenge.
      // Don't rely on the dashboard-layout MFA gate downstream: that
      // gate skips when ``isPublicRoute === true`` (which includes "/"),
      // so a redirect-to-"/" can land the user on the home page without
      // ever firing the gate. Doing the redirect here closes that hole
      // and matches the Supabase docs' recommended flow:
      //   getAuthenticatorAssuranceLevel → currentLevel=aal1 + nextLevel=aal2
      //   → router.replace('/auth/mfa-challenge')
      if (mfaRequired) {
        router.replace('/auth/mfa-challenge');
        return;
      }

      // No MFA required — let the existing useEffect handle redirect once
      // user+profile state propagates. Set the waiting flag to keep the
      // spinner visible during React's async state-update window.
      setIsLoading(false);
      setIsWaitingForProfile(true);

      setTimeout(() => {
        setIsWaitingForProfile(false);
      }, 500);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
      setIsWaitingForProfile(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <Image
              src="/logo.png"
              alt="CareCaller"
              width={48}
              height={48}
              className="h-12 w-12 rounded-xl object-cover"
            />
          </div>
          <CardTitle className="text-2xl text-center">Welcome Back</CardTitle>
          <CardDescription className="text-center">
            Sign in to CareCaller.ai Dashboard
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {successMessage && (
              <div className="flex items-center gap-2 p-3 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 rounded-lg">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {wasKicked && !error && (
              <div className="flex items-start gap-2 p-3 text-sm text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300 rounded-lg">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Another tab is now using this account. You can close this tab,
                  or sign in here to take the session back.
                </span>
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
                {sessionTakenOver && (
                  <button
                    type="button"
                    onClick={async () => {
                      // Stale JWT in localStorage is still good enough to
                      // authenticate the revoke — only the per-app SID has
                      // diverged. After this returns, drop the
                      // ?session_taken_over=1 query so this same tab can
                      // log in fresh without bouncing back here.
                      setIsRevokingOthers(true);
                      try {
                        await authApi.signOutAll();
                      } catch { /* signOutAll swallows + clears tokens itself */ }
                      setIsRevokingOthers(false);
                      setError(null);
                      // HARD reload, not router.replace. The auth-context
                      // still holds user/profile in React state — a soft
                      // nav would re-render /login, fire the
                      // "redirect-if-authenticated" useEffect above
                      // (line 79-87), and bounce the user straight back
                      // to '/' before they can type credentials. From
                      // there homepage tries an API call with the now-
                      // cleared tokens, 401s, eventually lands them back
                      // at /login — but only after several wasted seconds
                      // and a confusing trip through "home". A full
                      // window.location.replace() drops every piece of
                      // React state in one shot and lands them on a
                      // pristine login form. Reported 2026-06-03.
                      window.location.replace('/login');
                    }}
                    disabled={isRevokingOthers || isLoading}
                    className="text-xs font-medium underline underline-offset-2 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  >
                    {isRevokingOthers ? 'Revoking other sessions…' : 'Sign out of all devices and continue here'}
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-sm text-primary hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Cloudflare Turnstile. Mostly invisible — only suspicious
                sessions get an interactive challenge. The key bump
                forces a fresh widget after a failed submit so the
                user can retry without a page reload. */}
            <div className="flex justify-center pt-2">
              <TurnstileWidget
                key={captchaResetKey}
                action="login"
                onToken={setCaptchaToken}
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4 pt-6">
            <Button
              type="submit"
              className="w-full h-10"
              disabled={isLoading || !captchaToken}
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
